'use client';

import { useState, useCallback, useEffect } from 'react';
import { useSession } from 'next-auth/react';

import { showWarningToast, showSuccessToast } from '@/components/ui/sonner';
import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';

interface CollectionData {
  status?: string;
  rating?: number;
  isInCollection: boolean;
  addedAt?: string;
  notes?: string;
  isPrivate?: boolean;
  private?: boolean;
}

export function useCollections(
  postId: string,
  checkStatus: boolean = true,
  initialData?: CollectionData,
) {
  const [collectionData, setCollectionData] = useState<CollectionData>(
    initialData
      ? { ...initialData, isInCollection: initialData?.status !== undefined }
      : { isInCollection: false },
  );
  const [isLoading, setIsLoading] = useState(false);
  const { data: session } = useSession();

  const checkCollectionStatus = useCallback(async () => {
    if (!session?.user || !session.accessToken) return;
    try {
      const response = await fetch(
        buildDirectBackendUrl(`/api/users/collections/check?postId=${postId}`),
        {
          headers: createDirectBackendHeaders({
            accessToken: session.accessToken,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to check collection status');
      }

      const data = await response.json();

      if (data.inCollection) {
        setCollectionData({
          isInCollection: true,
          status: data.collection.status,
          rating: data.collection.rating,
          addedAt: data.collection.addedAt,
          notes: data.collection.notes,
          isPrivate: data.collection.isPrivate,
          private: data.collection.private,
        });
      } else {
        setCollectionData({
          isInCollection: false,
        });
      }
    } catch (error) {
      console.error('Error checking collection status:', error);
    }
  }, [postId, session?.accessToken, session?.user]);

  useEffect(() => {
    if (!checkStatus) return;
    checkCollectionStatus();
  }, [checkCollectionStatus, checkStatus]);

  const addToCollection = useCallback(
    async (status: string, isPrivate?: boolean) => {
      setIsLoading(true);
      try {
        if (!session?.accessToken) {
          throw new Error('Требуется авторизация');
        }

        const response = await fetch(buildDirectBackendUrl('/api/users/collections/add'), {
          method: 'POST',
          headers: createDirectBackendHeaders({
            accessToken: session.accessToken,
            hasJsonContentType: true,
          }),
          body: JSON.stringify({
            postId: parseInt(postId, 10),
            status,
            isPrivate,
          }),
        });
        console.log({ response });
        if (!response.ok) {
          throw new Error('Failed to add to collection');
        }

        setCollectionData((prev) => ({
          ...prev,
          status,
          isInCollection: true,
          addedAt: new Date().toISOString(),
          isPrivate,
        }));

        showSuccessToast('Работа добавлена в библиотеку');

        // Отслеживаем чтение асинхронно только для статусов "читаю", "прочитано" и "буду читать"
        if (status === 'reading' || status === 'read' || status === 'want-to-read') {
          fetch(buildDirectBackendUrl('/api/reading-progress/read'), {
            method: 'POST',
            headers: createDirectBackendHeaders({
              accessToken: session?.accessToken,
              hasJsonContentType: true,
            }),
            body: JSON.stringify({
              postId: postId,
            }),
          }).catch((error) => {
            console.error('Failed to track read:', error);
          });
        }
      } catch (error) {
        console.error('Error adding to collection:', error);
        showWarningToast('Не удалось добавить в библиотеку');
      } finally {
        setIsLoading(false);
      }
    },
    [postId, session?.accessToken, session?.user],
  );

  const updateStatus = useCallback(
    async (status: string, isPrivate?: boolean) => {
      setIsLoading(true);
      try {
        if (!session?.accessToken) {
          throw new Error('Требуется авторизация');
        }

        const response = await fetch(buildDirectBackendUrl('/api/users/collections/status'), {
          method: 'PUT',
          headers: createDirectBackendHeaders({
            accessToken: session.accessToken,
            hasJsonContentType: true,
          }),
          body: JSON.stringify({
            postId: parseInt(postId, 10),
            status,
            isPrivate,
          }),
        });
        if (!response.ok) {
          throw new Error('Failed to update status');
        }

        setCollectionData((prev) => ({
          ...prev,
          status,
          isPrivate,
        }));

        showSuccessToast('Статус в библиотеке изменён');
      } catch (error) {
        console.error('Error updating status:', error);
        showWarningToast('Не удалось обновить статус');
      } finally {
        setIsLoading(false);
      }
    },
    [postId, session?.accessToken],
  );

  const updateRating = useCallback(
    async (rating: number) => {
      setIsLoading(true);
      try {
        const response = await fetch(buildDirectBackendUrl('/api/users/collections/rating'), {
          method: 'PUT',
          headers: createDirectBackendHeaders({
            accessToken: session?.accessToken,
            hasJsonContentType: true,
          }),
          body: JSON.stringify({
            postId: parseInt(postId, 10),
            rating,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Rating update error:', {
            status: response.status,
            statusText: response.statusText,
            errorData,
          });
          throw new Error(`Failed to update rating: ${response.status} ${response.statusText}`);
        }

        setCollectionData((prev) => ({
          ...prev,
          rating,
        }));

        showSuccessToast('Ваша оценка сохранена');
      } catch (error) {
        console.error('Error updating rating:', error);
        showWarningToast('Не удалось обновить рейтинг');
      } finally {
        setIsLoading(false);
      }
    },
    [postId, session?.accessToken],
  );

  const removeFromCollection = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!session?.accessToken) {
        throw new Error('Требуется авторизация');
      }
      const response = await fetch(
        buildDirectBackendUrl(`/api/users/collections?postId=${parseInt(postId, 10)}`),
        {
          method: 'DELETE',
          headers: createDirectBackendHeaders({
            accessToken: session.accessToken,
          }),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to remove from collection');
      }

      setCollectionData({
        isInCollection: false,
      });

      showSuccessToast('Работа удалена из вашей библиотеки');
    } catch (error) {
      console.error('Error removing from collection:', error);
      showWarningToast('Не удалось удалить из библиотеки');
    } finally {
      setIsLoading(false);
    }
  }, [postId, session?.accessToken]);

  return {
    collectionData,
    isLoading,
    addToCollection,
    updateStatus,
    updateRating,
    removeFromCollection,
    checkCollectionStatus,
  };
}
